import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, Observable, of, take, timeout } from 'rxjs';
import { UserStore } from '../store/user.store';
import { PermissionService } from 'core/services/permission.service';
import { DashboardAccess, resolveDefaultDashboard } from '../dashboard-defaults';

/** How long to wait for the user's profiles to load before settling on the fallback. */
const PROFILE_WAIT_MS = 5000;

/**
 * `/dashboard` itself renders nothing: it redirects to the profile's default home dashboard
 * (`profileAdminSettings.defaultDashboard`), or Payments.
 *
 * It WAITS for the active profile, because login starts loading the user's profiles and navigates
 * here without waiting for them — reading the store immediately would send everyone to Payments
 * on their first visit. If the profile never arrives the fallback is Payments, never an error.
 */
export const defaultDashboardRedirect: CanActivateFn = (): UrlTree | Observable<UrlTree> => {
  const store = inject(UserStore);
  const router = inject(Router);
  const permissions = inject(PermissionService);

  const target = (): UrlTree => {
    const profile = store.activeProfile();
    const roles = profile?.roles ?? [];
    const isSystemAdmin =
      !!store.userInfo()?.userInfo?.userSettings?.isSystemAdmin || roles.includes('SYSTEM_ADMIN');
    const access: DashboardAccess = {
      settings: profile?.profileDetail?.profileAdminSettings,
      erpEnabled: !!profile?.profileDetail?.erpEnabled,
      canAccessErp: isSystemAdmin || roles.includes('GROUP_ERP_Administrator'),
      canReadLedger: permissions.hasPermission('GET_TRANSACTION_STATS'),
    };
    return router.createUrlTree(['/dashboard', resolveDefaultDashboard(access)]);
  };

  if (store.activeProfile()) return target();
  return toObservable(store.activeProfile).pipe(
    filter((p) => !!p),
    take(1),
    timeout({ first: PROFILE_WAIT_MS, with: () => of(null) }),
    map(() => target()),
  );
};
